import React from 'react'
import { Search } from 'lucide-react'
import { Table, Tr, Th, Td } from '@/shared/ui/Table'
import { Badge, Card, CardContent, Input, Select } from '@/shared/ui'
import { listTrendShadow, listTrendShadowEvents, type TrendShadowEvent, type TrendShadowRow } from '../services/trendShadowService'
import TrendPointsModal from '../components/TrendPointsModal'

const TF_ORDER = ['5', '15', '30', '60', '240', '720', 'D', 'W', 'M']
const TF_LABEL: Record<string, string> = { '5': '5m', '15': '15m', '30': '30m', '60': '1h', '240': '4h', '720': '12h', D: '1D', W: '1S', M: '1M' }
const PHASE_LABEL: Record<string, string> = {
  range: 'lateral',
  pivoting_up: 'virando para cima',
  pivoting_down: 'virando para baixo',
  seeking_higher_low: 'buscando fundo mais alto',
  seeking_lower_high: 'buscando topo mais baixo',
}

function TrendBadge({ value }: { value: string | null }) {
  if (value === 'bullish') return <Badge variant="soft" color="success">Alta</Badge>
  if (value === 'bearish') return <Badge variant="soft" color="error">Baixa</Badge>
  if (!value) return <span className="text-foreground/50">—</span>
  return <Badge variant="soft">{value === 'neutral' ? 'Neutra' : value}</Badge>
}

function fmtDate(v: string | null) {
  if (!v) return '—'
  return new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export default function TrendStructureShadowPage() {
  const [rows, setRows] = React.useState<TrendShadowRow[]>([])
  const [events, setEvents] = React.useState<TrendShadowEvent[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [tf, setTf] = React.useState('')
  const [onlyDiff, setOnlyDiff] = React.useState(false)
  const [q, setQ] = React.useState('')
  const [selected, setSelected] = React.useState<TrendShadowRow | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [r, e] = await Promise.all([listTrendShadow(), listTrendShadowEvents()])
      setRows(r)
      setEvents(e)
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao carregar a comparação')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { load() }, [load])

  const scoped = React.useMemo(() => rows.filter((r) => !tf || r.timeframe === tf), [rows, tf])
  const compared = scoped.filter((r) => r.trend_v2 && r.trend_atual)
  const diffs = compared.filter((r) => r.diverge).length
  const lastUpdate = rows.reduce<string | null>((acc, r) => (r.computed_at && (!acc || r.computed_at > acc) ? r.computed_at : acc), null)

  const filtered = React.useMemo(() => {
    const term = q.trim().toUpperCase()
    return scoped
      .filter((r) => !onlyDiff || r.diverge)
      .filter((r) => !term || r.symbol.includes(term))
      .sort((a, b) => a.symbol.localeCompare(b.symbol) || TF_ORDER.indexOf(a.timeframe) - TF_ORDER.indexOf(b.timeframe))
  }, [scoped, onlyDiff, q])

  return (
    <div className="mx-auto w-full max-w-[1550px]">
      <TrendPointsModal row={selected} onClose={() => setSelected(null)} />
      <div className="mb-6">
        <div className="text-2xl font-semibold">Trend Structure (modo sombra)</div>
        <div className="text-sm text-foreground/70">
          Tendência calculada pelo motor do Vela comparada com a tendência atual do Cripto Alert. Clique em uma linha para ver o topo e o fundo que definem a tendência em cada modelo. Só o master vê; nada aqui afeta clientes, sinais ou notificações.
        </div>
      </div>

      {error && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card><CardContent className="p-4"><div className="text-xs text-foreground/60">Pares calculados</div><div className="text-2xl font-semibold">{scoped.length}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-foreground/60">Mesma tendência</div><div className="text-2xl font-semibold">{compared.length ? Math.round(((compared.length - diffs) / compared.length) * 100) : 0}%</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-foreground/60">Tendências diferentes</div><div className="text-2xl font-semibold">{diffs}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-foreground/60">Última atualização</div><div className="text-2xl font-semibold">{fmtDate(lastUpdate)}</div></CardContent></Card>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar moeda" className="pl-9" />
        </div>
        <Select value={tf} onChange={(e) => setTf(e.target.value)} className="w-40">
          <option value="">Todos os tempos</option>
          {TF_ORDER.map((t) => <option key={t} value={t}>{TF_LABEL[t]}</option>)}
        </Select>
        <label className="flex items-center gap-2 text-sm text-foreground/80">
          <input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} />
          Só tendências diferentes
        </label>
      </div>

      <Table>
        <thead>
          <Tr>
            <Th>Moeda</Th>
            <Th>Tempo</Th>
            <Th>Tendência atual</Th>
            <Th>Trend Structure</Th>
            <Th>Fase</Th>
            <Th>Último pivô (Trend Structure)</Th>
            <Th>Candle</Th>
          </Tr>
        </thead>
        <tbody>
          {filtered.map((r) => (
            <Tr
              key={`${r.symbol}-${r.timeframe}`}
              onClick={() => setSelected(r)}
              title="Ver topo e fundo que definem a tendência"
              className={`cursor-pointer hover:bg-foreground/5 ${r.diverge ? 'bg-amber-500/5' : ''}`}
            >
              <Td className="font-medium">{r.symbol}</Td>
              <Td>{TF_LABEL[r.timeframe] ?? r.timeframe}</Td>
              <Td><TrendBadge value={r.trend_atual} /></Td>
              <Td><TrendBadge value={r.trend_v2} /></Td>
              <Td className="text-foreground/80">{r.fase_v2 ? PHASE_LABEL[r.fase_v2] ?? r.fase_v2 : '—'}</Td>
              <Td className="text-foreground/80">{r.pivo_v2 ? `${r.pivo_v2} em ${fmtDate(r.pivo_v2_em)}` : '—'}</Td>
              <Td className="text-foreground/60">{fmtDate(r.last_candle_open)}</Td>
            </Tr>
          ))}
          {!loading && filtered.length === 0 && (
            <Tr><Td colSpan={7} className="py-10 text-center text-sm text-foreground/70">Nenhum resultado ainda. O cálculo roda a cada minuto.</Td></Tr>
          )}
          {loading && (
            <Tr><Td colSpan={7} className="py-10 text-center text-sm text-foreground/70">Carregando...</Td></Tr>
          )}
        </tbody>
      </Table>

      <div className="mb-3 mt-10 text-lg font-semibold">Últimas viradas de tendência (Trend Structure)</div>
      <Table>
        <thead>
          <Tr>
            <Th>Quando</Th>
            <Th>Moeda</Th>
            <Th>Tempo</Th>
            <Th>De</Th>
            <Th>Para</Th>
            <Th>Fase</Th>
          </Tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <Tr key={e.id}>
              <Td className="text-foreground/70">{fmtDate(e.created_at)}</Td>
              <Td className="font-medium">{e.symbol}</Td>
              <Td>{TF_LABEL[e.timeframe] ?? e.timeframe}</Td>
              <Td><TrendBadge value={e.from_trend} /></Td>
              <Td><TrendBadge value={e.to_trend} /></Td>
              <Td className="text-foreground/80">{e.phase ? PHASE_LABEL[e.phase] ?? e.phase : '—'}</Td>
            </Tr>
          ))}
          {!loading && events.length === 0 && (
            <Tr><Td colSpan={6} className="py-8 text-center text-sm text-foreground/70">Nenhuma virada registrada ainda.</Td></Tr>
          )}
        </tbody>
      </Table>
    </div>
  )
}
